import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLeaf, 
  faCertificate, 
  faShippingFast, 
  faShieldAlt,
  faChevronDown
} from '@fortawesome/free-solid-svg-icons';

interface HeroProps {
  title: string;
  subtitle: string;
  ctaText1: string;
  ctaLink1: string;
  ctaText2?: string;
  ctaLink2?: string;
  backgroundImage?: string;
}

const Hero: React.FC<HeroProps> = ({
  title,
  subtitle,
  ctaText1,
  ctaLink1,
  ctaText2,
  ctaLink2,
  backgroundImage = require('../assets/images/background.jpg')
}) => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background with overlay */}
      <div className="absolute inset-0 bg-black">
        <div 
          className="absolute inset-0 bg-cover bg-center md:bg-fixed" 
          style={{ 
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.6,
            filter: 'brightness(0.7)'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-transparent"></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10 py-20 md:py-32 mt-20 md:mt-28">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6">
            <span className="bg-gradient-to-r from-[#C2C764] via-white to-[#2b603e] text-transparent bg-clip-text">
              {title.split(' ').slice(0, -1).join(' ')}
            </span>
            <br />
            <span className="bg-gradient-to-r from-white via-[#C2C764] to-white text-transparent bg-clip-text">
              {title.split(' ').slice(-1)}
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
            {subtitle}
          </p>
          
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
            <Link 
              to={ctaLink1} 
              className="bg-[#2b603e] hover:bg-[#1a3d25] text-white font-bold py-4 px-8 rounded-full inline-block transition-all duration-300 transform hover:scale-105 min-w-[200px]"
            >
              {ctaText1}
            </Link>
            {ctaText2 && ctaLink2 && (
              <Link 
                to={ctaLink2} 
                className="bg-transparent hover:bg-white/10 text-white border-2 border-white font-bold py-4 px-8 rounded-full inline-block transition-all duration-300 transform hover:scale-105 min-w-[200px]"
              >
                {ctaText2}
              </Link>
            )}
          </div>

          {/* Featured Badges */}
          <div className="mt-16 flex flex-wrap justify-center gap-6">
            <div className="flex items-center space-x-2 text-white/90 hover:text-[#C2C764] transition-all duration-300">
              <FontAwesomeIcon icon={faLeaf} className="text-lg" />
              <p className="text-sm">100% Natural</p>
            </div>
            <div className="flex items-center space-x-2 text-white/90 hover:text-[#C2C764] transition-all duration-300">
              <FontAwesomeIcon icon={faCertificate} className="text-lg" />
              <p className="text-sm">Premium Quality</p>
            </div>
            <div className="flex items-center space-x-2 text-white/90 hover:text-[#C2C764] transition-all duration-300">
              <FontAwesomeIcon icon={faShippingFast} className="text-lg" />
              <p className="text-sm">Fast Shipping</p>
            </div>
            <div className="flex items-center space-x-2 text-white/90 hover:text-[#C2C764] transition-all duration-300">
              <FontAwesomeIcon icon={faShieldAlt} className="text-lg" />
              <p className="text-sm">Lab Tested</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white text-center">
        <p className="text-sm mb-2">Scroll to explore</p>
        <FontAwesomeIcon icon={faChevronDown} className="animate-bounce text-xl" />
      </div>
    </section>
  );
};

export default Hero;